import React, { useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { PageHeader, PrimaryButton, GhostButton, GlassCard } from "@/components/Primitives";
import RangeSelect, { DEFAULT_RANGE } from "@/components/RangeSelect";
import ExportButton from "@/components/ExportButton";
import Modal, { inputCls, Field } from "@/components/Modal";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import { Plus, X, Edit3, Trash2, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";

const blank = { company_name: "", contact_name: "", email: "", phone: "", website: "", address: "", notes: "", stripe_customer_id: "" };

function ClientStripeInvoices({ clientId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!clientId) return;
    api.get(`/invoices`, { params: { client_id: clientId } }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [clientId]);
  if (items === null) return <div className="text-xs text-white/40">Loading Stripe invoices…</div>;
  if (items.length === 0) {
    return (
      <div className="text-xs text-white/45">
        No Stripe invoices for this client.
        <span className="block mt-1 text-white/30">Set the Stripe Customer ID above and sync from the Billing page.</span>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-white/[0.07] overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-white/[0.03]">
          <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
            <th className="py-2 px-3">Number</th>
            <th className="py-2 px-3">Status</th>
            <th className="py-2 px-3 text-right">Total</th>
            <th className="py-2 px-3 text-right">Paid</th>
            <th className="py-2 px-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.05]">
          {items.map((i) => (
            <tr key={i.stripe_id} data-testid={`client-inv-${i.stripe_id}`}>
              <td className="py-2 px-3 text-white">{i.number || i.stripe_id.slice(-8)}</td>
              <td className="py-2 px-3 text-white/55 uppercase tracking-wider">{i.status}</td>
              <td className="py-2 px-3 text-right">{fmtMoney(i.total)}</td>
              <td className="py-2 px-3 text-right text-emerald-300">{fmtMoney(i.amount_paid)}</td>
              <td className="py-2 px-3 text-right">
                {i.hosted_invoice_url && (
                  <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer"
                     className="text-[#007AFF] hover:text-[#3596ff] inline-flex"><ExternalLink className="w-3.5 h-3.5" /></a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientDialog({ open, initial, onClose, onSaved }) {
  const confirm = useConfirm();
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...blank, ...initial } : blank);
  }, [open, initial]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/clients/${initial.id}`, form);
      else await api.post("/clients", form);
      toast.success(isEdit ? "Client updated" : "Client created");
      onClose(); onSaved();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!isEdit) return;
    const ok = await confirm({
      title: "Delete this client?",
      description: `“${initial?.company_name || "This client"}” and links from projects/hosting/maintenance will lose their reference. This cannot be undone.`,
      confirmLabel: "Delete client",
    });
    if (!ok) return;
    try { await api.delete(`/clients/${initial.id}`); toast.success("Deleted"); onClose(); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Modal open={open} onClose={onClose} size="xl">
      <form onSubmit={submit} className="p-8" data-testid="client-dialog">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-light">{isEdit ? "Edit client" : "New client"}</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[["Company", "company_name"], ["Contact Name", "contact_name"], ["Email", "email"], ["Phone", "phone"], ["Website", "website"], ["Address", "address"]].map(([label, k]) => (
            <Field key={k} label={label}>
              <input data-testid={`client-${k}`} className={inputCls} value={form[k] || ""} onChange={update(k)} required={k === "company_name"} />
            </Field>
          ))}
          <div className="sm:col-span-2">
            <Field label="Stripe Customer ID">
              <input data-testid="client-stripe-id" className={inputCls} value={form.stripe_customer_id || ""} onChange={update("stripe_customer_id")} placeholder="cus_1A2B… (tracked only — Stripe handles emails)" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Notes"><textarea rows={3} className={inputCls} value={form.notes || ""} onChange={update("notes")} /></Field>
          </div>
        </div>

        {isEdit && (
          <div className="mt-8 pt-6 border-t border-white/[0.06]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#007AFF]/80 mb-3 flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Stripe Invoices</div>
            <ClientStripeInvoices clientId={initial.id} />
          </div>
        )}

        {isEdit && (
          <div className="mt-8 pt-6 border-t border-white/[0.06]">
            <AttachmentsPanel parentType="client" parentId={initial.id} testIdPrefix="client-att" />
          </div>
        )}

        <div className="flex items-center justify-between mt-8">
          {isEdit ? (
            <button type="button" onClick={remove} className="text-rose-400 hover:text-rose-300 text-sm inline-flex items-center gap-1.5" data-testid="client-delete"><Trash2 className="w-4 h-4" /> Delete</button>
          ) : <div />}
          <div className="flex items-center gap-3">
            <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton type="submit" disabled={busy} data-testid="client-save">{busy ? "Saving…" : (isEdit ? "Save" : "Create")}</PrimaryButton>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const load = async () => { const { data } = await api.get("/clients"); setClients(data); };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (sessionStorage.getItem("equinox_quick_action") === "client") {
      sessionStorage.removeItem("equinox_quick_action");
      setEditing(null); setOpen(true);
    }
  }, []);

  return (
    <div data-testid="clients-page">
      <PageHeader
        title="Clients"
        subtitle="The companies you've earned. Track lifetime value and active engagements."
        testId="clients-header"
        action={
          <div className="flex items-center gap-2.5">
            <RangeSelect value={range} onChange={setRange} testIdPrefix="clients-range" />
            <ExportButton endpoint="/exports/clients" range={range} testId="clients-export-btn" label="Export" />
            <PrimaryButton onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-client-btn"><Plus className="w-4 h-4" /> New Client</PrimaryButton>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {clients.map((c) => (
          <GlassCard key={c.id} className="cursor-pointer hover:-translate-y-0.5 transition relative group" testId={`client-${c.id}`}
                     onClick={() => { setEditing(c); setOpen(true); }}>
            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#007AFF] to-[#0050C8] flex items-center justify-center text-sm font-semibold">
                {c.company_name?.slice(0, 1)}
              </div>
              <div className="flex flex-col gap-1 items-end">
                {c.hosting_active && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/20">Hosting</span>}
                {c.maintenance_active && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-400/10 text-violet-300 border border-violet-400/20">Maint.</span>}
              </div>
            </div>
            <div className="font-display text-lg text-white">{c.company_name}</div>
            <div className="text-xs text-white/45">{c.contact_name || "—"}</div>
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/[0.05]">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Lifetime</div>
                <div className="font-display text-lg text-white">{fmtMoney(c.lifetime_revenue)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Projects</div>
                <div className="font-display text-lg text-white">{c.active_projects}</div>
              </div>
            </div>
            <Edit3 className="absolute top-4 right-4 w-3.5 h-3.5 text-white/0 group-hover:text-white/50 transition" />
          </GlassCard>
        ))}
        {clients.length === 0 && <GlassCard className="col-span-full text-center text-white/40">No clients yet.</GlassCard>}
      </div>

      <ClientDialog open={open} initial={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={load} />
    </div>
  );
}
